import { useEffect, useRef, useState } from "react";
import { Download, FileSpreadsheet, RefreshCw, ScanText, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const WCOLS = ["925", "10K", "14K", "18K", "22K", "PT", "24K"];
const STATUS_STYLE = {
  clean: "border-emerald-300 text-emerald-700 bg-emerald-50",
  flagged: "border-amber-300 text-amber-700 bg-amber-50",
  rejected: "border-red-300 text-red-600 bg-red-50",
};

const dl = (blob, name) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

export default function CadImport() {
  const [tab, setTab] = useState("extractor");
  return (
    <div className="space-y-5" data-testid="cad-import-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">CAD Catalogue Import</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Spec-sheet extractor (template OCR + volume consistency check) and the CSV template importer.
          Nothing reaches live pricing without review.
        </p>
      </div>
      <div className="flex gap-2">
        {[["extractor", "Spec Sheet Extractor", ScanText], ["xlsx", "Shopify Excel Import", FileSpreadsheet], ["csv", "CSV Import / Export", Download]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm border flex items-center gap-2 transition-colors ${tab === k ? "bg-stone-900 text-white border-stone-900" : "bg-white border-neutral-300 hover:border-stone-500"}`}
            data-testid={`cad-tab-${k}`}>
            <Icon size={15} /> {l}
          </button>
        ))}
      </div>
      {tab === "extractor" ? <Extractor /> : tab === "xlsx" ? <XlsxImport /> : <CsvImport />}
    </div>
  );
}

/* ---------------- Spec Sheet Extractor ---------------- */

function Extractor() {
  const [cfg, setCfg] = useState(null);
  const [batches, setBatches] = useState([]);
  const [batch, setBatch] = useState(null);
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({});
  const [prefix, setPrefix] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const loadSettings = () => api.get("/admin/cad/extract/settings").then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e)));
  const loadBatches = () => api.get("/admin/cad/extract/batches").then((r) => setBatches(r.data)).catch(() => {});
  const loadBatch = (id) => api.get(`/admin/cad/extract/batches/${id}`).then((r) => {
    setBatch(r.data);
    setRows(r.data.rows || []);
    setSel({});
  }).catch((e) => toast.error(errMsg(e)));

  useEffect(() => { loadSettings(); loadBatches(); }, []);
  useEffect(() => {
    const running = batches.some((b) => b.status === "running") || batch?.status === "running";
    if (!running) return;
    const t = setInterval(() => { loadBatches(); if (batch) loadBatch(batch.id || batch._id); }, 3000);
    return () => clearInterval(t);
  }, [batches, batch?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    try {
      const r = await api.post("/admin/cad/extract/upload", fd);
      toast.success(`Extraction started — ${r.data.total} sheets`);
      loadBatches();
      loadBatch(r.data.batch_id);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const runBucket = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/cad/extract/bucket", { prefix });
      toast.success(`Extraction started — ${r.data.total} sheets from bucket`);
      loadBatches();
      loadBatch(r.data.batch_id);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const setRow = (idx, patch) => setRows((rs) => rs.map((r) => (r.index === idx ? { ...r, ...patch } : r)));
  const setWeight = (idx, key, val) =>
    setRows((rs) => rs.map((r) => {
      if (r.index !== idx) return r;
      const weights = { ...(r.weights || {}) };
      const cur = weights[key] || {};
      weights[key] = { ...cur, value: val === "" ? null : parseFloat(val) };
      return { ...r, weights };
    }));

  const saveRow = async (row) => {
    const weights = {};
    for (const [k, info] of Object.entries(row.weights || {})) {
      const v = typeof info === "object" ? info.value : info;
      weights[k] = v === null || v === undefined || v === "" || Number.isNaN(v) ? null : v;
    }
    try {
      const r = await api.patch("/admin/cad/extract/rows", {
        batch_id: batch.id || batch._id, index: row.index,
        edits: { design_number: row.design_number, ring_size: row.ring_size, derived_9k: row.derived_9k, weights },
      });
      setRow(row.index, r.data);
      toast.success(`${row.design_number || "Row"} saved — volume check re-run`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const accept = async (indexes) => {
    try {
      const r = await api.post("/admin/cad/extract/accept", { batch_id: batch.id || batch._id, rows: indexes?.length ? indexes : null });
      toast.success(`${r.data.applied} rows written to products (drafts — never live pricing without review)`);
      loadBatch(batch.id || batch._id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportCsv = async () => {
    const r = await api.get(`/admin/cad/extract/export?batch_id=${batch.id || batch._id}`, { responseType: "blob" });
    dl(new Blob([r.data], { type: "text/csv" }), `cad-extract-${batch.id || batch._id}.csv`);
  };

  const saveSettings = async () => {
    try {
      const r = await api.put("/admin/cad/extract/settings", cfg);
      setCfg(r.data);
      toast.success("Extractor settings saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const summary = batch?.summary || {};
  const eligible = rows.filter((r) => !r.accepted && (r.status === "clean" || r.state === "confirmed"));

  return (
    <div className="space-y-5">
      <section className="bg-white border border-neutral-200 p-5 flex flex-wrap items-end gap-3" data-testid="extractor-controls">
        <div>
          <Label>Upload spec sheet images (max 100)</Label>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={(e) => upload(e.target.files)}
            className="block text-xs mt-1" data-testid="extract-upload-input" />
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label>…or bucket prefix</Label>
            <Input value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="e.g. specs/ (empty = whole bucket)" className="h-9 w-56" data-testid="extract-bucket-prefix" />
          </div>
          <Button variant="outline" size="sm" onClick={runBucket} disabled={busy} className="h-9" data-testid="extract-bucket-run">
            <ScanText size={14} className="mr-1" /> Scan bucket
          </Button>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setShowSettings((s) => !s)} className="ml-auto" data-testid="extract-settings-toggle">
          {showSettings ? "Hide" : "Regions / densities / tolerance"}
        </Button>
      </section>

      {showSettings && cfg && (
        <section className="bg-white border border-neutral-200 p-5 space-y-4" data-testid="extract-settings">
          <div className="flex items-center gap-4">
            <div>
              <Label>Volume tolerance %</Label>
              <Input type="number" step="0.5" value={cfg.tolerance_pct}
                onChange={(e) => setCfg({ ...cfg, tolerance_pct: parseFloat(e.target.value) || 0 })} className="h-8 w-24" data-testid="cfg-tolerance" />
            </div>
            <div>
              <Label>Densities (g/cc)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {Object.entries(cfg.densities).map(([k, v]) => (
                  <span key={k} className="flex items-center gap-1 text-xs">
                    <span className="font-mono">{k}</span>
                    <Input type="number" step="0.01" value={v}
                      onChange={(e) => setCfg({ ...cfg, densities: { ...cfg.densities, [k]: parseFloat(e.target.value) || 0 } })}
                      className="h-7 w-20" data-testid={`cfg-density-${k.toLowerCase()}`} />
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div>
            <Label>Template regions (fractions of sheet width/height)</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
              {Object.entries(cfg.regions).map(([name, r]) => (
                <div key={name} className="flex items-center gap-1 text-xs border border-neutral-100 p-2">
                  <span className="font-mono w-28 shrink-0">{name}</span>
                  {["x0", "x1", "y0", "y1"].map((f) => (
                    <Input key={f} type="number" step="0.005" value={r[f]} title={f}
                      onChange={(e) => setCfg({ ...cfg, regions: { ...cfg.regions, [name]: { ...r, [f]: parseFloat(e.target.value) || 0 } } })}
                      className="h-7 w-16" data-testid={`cfg-region-${name}-${f}`} />
                  ))}
                  <Input type="number" value={r.psm} title="psm"
                    onChange={(e) => setCfg({ ...cfg, regions: { ...cfg.regions, [name]: { ...r, psm: parseInt(e.target.value) || 7 } } })}
                    className="h-7 w-12" data-testid={`cfg-region-${name}-psm`} />
                </div>
              ))}
            </div>
          </div>
          <Button size="sm" onClick={saveSettings} data-testid="cfg-save">Save extractor settings</Button>
        </section>
      )}

      <section className="bg-white border border-neutral-200 p-5" data-testid="extract-batches">
        <h2 className="font-heading font-semibold mb-3">Extraction batches</h2>
        <div className="flex flex-wrap gap-2">
          {batches.map((b) => (
            <button key={b.id || b._id} onClick={() => loadBatch(b.id || b._id)}
              className={`border px-3 py-2 text-xs text-left ${(batch?.id || batch?._id) === (b.id || b._id) ? "border-stone-900 bg-stone-50" : "border-neutral-200"}`}
              data-testid={`extract-batch-${b.id || b._id}`}>
              <div className="font-mono">{b.label}</div>
              <div className="text-neutral-500 mt-0.5">
                {b.status === "running" ? `extracting ${b.done}/${b.total}…` :
                  `${b.summary?.clean || 0} clean · ${b.summary?.flagged || 0} flagged · ${b.summary?.rejected || 0} rejected · ${b.summary?.accepted || 0} accepted`}
              </div>
            </button>
          ))}
          {!batches.length && <p className="text-sm text-neutral-400">No batches yet — upload sheets or scan the bucket.</p>}
        </div>
      </section>

      {batch && (
        <section className="bg-white border border-neutral-200 p-5 space-y-3" data-testid="extract-review">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-heading font-semibold">Review table — {batch.label}</h2>
            <span className="text-xs font-mono" data-testid="extract-accuracy">
              {batch.status === "running" ? `extracting ${batch.done}/${batch.total}…` :
                `accuracy: ${summary.clean || 0} clean / ${summary.flagged || 0} flagged / ${summary.rejected || 0} rejected of ${rows.length}`}
            </span>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={exportCsv} data-testid="extract-export"><Download size={14} className="mr-1" /> Export CSV</Button>
              <Button variant="outline" size="sm" disabled={!Object.values(sel).some(Boolean)}
                onClick={() => accept(Object.keys(sel).filter((k) => sel[k]).map(Number))} data-testid="extract-accept-selected">
                Accept selected
              </Button>
              <Button size="sm" className="bg-stone-900 text-white" disabled={!eligible.length}
                onClick={() => accept(null)} data-testid="extract-accept-all">
                Accept all clean/confirmed ({eligible.length})
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[1200px]">
              <thead>
                <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
                  {["", "File", "Design no.", "Size", ...WCOLS, "9K (derived)", "Diamonds", "Status", ""].map((h) => <th key={h} className="py-2 pr-2">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.index} className={`border-b border-neutral-100 ${r.status === "rejected" ? "bg-red-50/40" : ""}`} data-testid={`extract-row-${r.index}`}>
                    <td className="py-1.5 pr-2">
                      <input type="checkbox" disabled={r.accepted || (r.status !== "clean" && r.state !== "confirmed")}
                        checked={!!sel[r.index]} onChange={(e) => setSel((s) => ({ ...s, [r.index]: e.target.checked }))}
                        data-testid={`extract-select-${r.index}`} />
                    </td>
                    <td className="py-1.5 pr-2 font-mono max-w-40 truncate" title={r.filename}>{r.filename}</td>
                    <td className="py-1.5 pr-2">
                      <Input value={r.design_number || ""} onChange={(e) => setRow(r.index, { design_number: e.target.value })}
                        className="h-7 w-28 font-mono" data-testid={`extract-design-${r.index}`} />
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input value={r.ring_size || ""} onChange={(e) => setRow(r.index, { ring_size: e.target.value })} className="h-7 w-14" />
                    </td>
                    {WCOLS.map((k) => {
                      const info = (r.weights || {})[k] || {};
                      const flagged = info.state === "flagged";
                      return (
                        <td key={k} className="py-1.5 pr-2">
                          <Input value={info.value ?? ""} onChange={(e) => setWeight(r.index, k, e.target.value)}
                            className={`h-7 w-20 font-mono ${flagged ? "border-amber-400 bg-amber-50" : ""}`}
                            data-testid={`extract-wt-${r.index}-${k.toLowerCase()}`} />
                          {flagged && <div className="text-[10px] text-amber-700 font-mono mt-0.5">exp {info.expected} ({info.deviation_pct}% off)</div>}
                        </td>
                      );
                    })}
                    <td className="py-1.5 pr-2">
                      <Input value={r.derived_9k ?? ""} onChange={(e) => setRow(r.index, { derived_9k: e.target.value === "" ? null : parseFloat(e.target.value) })}
                        className="h-7 w-20 font-mono border-dashed" data-testid={`extract-derived-${r.index}`} />
                      <div className="text-[10px] text-amber-700 font-mono mt-0.5">derived{ r.derived_9k_auto && r.derived_9k !== r.derived_9k_auto ? ` (auto ${r.derived_9k_auto})` : ""}</div>
                    </td>
                    <td className="py-1.5 pr-2 font-mono">{(r.diamonds || []).length ? `${r.diamonds.length} rows · ${r.diamonds.reduce((s, d) => s + (d.total_ct || 0), 0).toFixed(2)}ct` : "—"}</td>
                    <td className="py-1.5 pr-2">
                      <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 border ${STATUS_STYLE[r.status] || ""}`} data-testid={`extract-status-${r.index}`}>
                        {r.accepted ? "accepted" : r.status}{r.state === "confirmed" && !r.accepted ? " · confirmed" : ""}
                      </span>
                    </td>
                    <td className="py-1.5"><Button variant="ghost" size="sm" onClick={() => saveRow(r)} data-testid={`extract-save-${r.index}`}>Save</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

/* ---------------- Shopify Excel Import (primary route) ---------------- */

function XlsxImport() {
  const [preview, setPreview] = useState(null);
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const fd = new FormData();
    const paths = [...files].map((f) => f.webkitRelativePath || f.name);
    [...files].forEach((f) => fd.append("files", f));
    fd.append("paths", JSON.stringify(paths));
    try {
      const r = await api.post("/admin/shopify-xlsx/preview", fd);
      setPreview(r.data);
      toast.success(`${r.data.products} products from ${r.data.variant_rows} rows — ${r.data.flagged_count} flagged`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const commit = async () => {
    try {
      await api.post("/admin/shopify-xlsx/commit", { batch_id: preview.batch_id });
      toast.success("Import running in background");
      const poll = setInterval(async () => {
        const r = await api.get(`/admin/shopify-xlsx/report/${preview.batch_id}`);
        setBatch(r.data);
        if (r.data.status === "done") clearInterval(poll);
      }, 2000);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="xlsx-import">
      <section className="bg-white border border-neutral-200 p-5 space-y-3">
        <p className="text-sm text-neutral-600">
          Upload the client's Shopify-format workbooks (.xlsx). Select the whole SIYAA folder — the path
          (<span className="font-mono">GOLD/LR</span>, <span className="font-mono">SILVER/ER</span>…) sets metal and category.
          Grouped on Variant SKU; Metal Weight drives pricing, Variant Grams (gross) never does. 18K/22K weights are
          derived from volume and marked <b>derived</b>. Data-quality failures import as drafts, never published.
        </p>
        <input ref={fileRef} type="file" accept=".xlsx" multiple
          // @ts-ignore folder upload preserves the category-hint path
          webkitdirectory="" onChange={(e) => upload(e.target.files)}
          className="block text-xs" data-testid="xlsx-upload-input" disabled={busy} />
        {busy && <p className="text-xs text-neutral-500">Parsing workbooks…</p>}
      </section>

      {preview && (
        <section className="bg-white border border-neutral-200 p-5 space-y-3" data-testid="xlsx-preview">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-heading font-semibold">{preview.products} products · {preview.variant_rows} variant rows</h2>
            <span className="text-xs text-neutral-500">{preview.creates} new · {preview.updates} updates · {preview.accepted} accepted</span>
            <Button size="sm" className="ml-auto bg-stone-900 text-white" onClick={commit} data-testid="xlsx-commit">Commit import</Button>
          </div>
          <div className="flex flex-wrap gap-2 text-xs font-mono" data-testid="xlsx-per-category">
            {Object.entries(preview.per_category || {}).map(([c, n]) => (
              <span key={c} className="border border-neutral-200 px-2 py-1">{c}: {n}</span>
            ))}
          </div>
          {preview.flagged_count > 0 && (
            <div className="border border-amber-200 bg-amber-50 p-3" data-testid="xlsx-flagged">
              <p className="text-xs font-semibold text-amber-800 mb-1">{preview.flagged_count} products flagged — imported as DRAFTS with reasons, never published:</p>
              <div className="max-h-48 overflow-y-auto text-xs font-mono space-y-0.5">
                {preview.flagged.map((f) => (
                  <div key={f.design_number} data-testid={`xlsx-flag-${f.design_number}`}>
                    <b>{f.design_number}</b> — {f.reasons.join("; ")}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {batch && (
        <section className="bg-white border border-neutral-200 p-5" data-testid="xlsx-report">
          <h2 className="font-heading font-semibold mb-2">Import report</h2>
          <p className="text-sm">
            Status: <b>{batch.status}</b>{batch.status === "running" ? ` — ${batch.done}/${batch.total}` : ""}
            {batch.final && <> · <span className="text-emerald-700">{batch.final.applied} applied</span> · <span className="text-red-600">{batch.final.failed} failed</span></>}
          </p>
          {batch.status === "done" && batch.report && (
            <p className="text-xs text-neutral-500 mt-1">
              {batch.report.skus} SKUs · {batch.report.variant_rows} rows processed · {batch.report.flagged?.length || 0} flagged (see above) · per-category counts shown above.
            </p>
          )}
        </section>
      )}
    </div>
  );
}

/* ---------------- CSV Import / Export ---------------- */

function CsvImport() {
  const [preview, setPreview] = useState(null);
  const [batches, setBatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const loadBatches = () => api.get("/admin/cad/batches").then((r) => setBatches(r.data)).catch(() => {});
  useEffect(() => { loadBatches(); }, []);

  const template = async () => {
    const r = await api.get("/admin/cad/template", { responseType: "blob" });
    dl(new Blob([r.data], { type: "text/csv" }), "siyaara-cad-catalogue-template.csv");
  };
  const exportAll = async () => {
    const r = await api.get("/admin/cad/export", { responseType: "blob" });
    dl(new Blob([r.data], { type: "text/csv" }), "siyaara-cad-catalogue-export.csv");
  };
  const uploadPreview = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/cad/preview", fd);
      setPreview(r.data);
      toast.success(`${r.data.accepted}/${r.data.total} rows accepted`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const commit = async () => {
    try {
      await api.post("/admin/cad/commit", { batch_id: preview.batch_id });
      toast.success("Import committed — running in background");
      setPreview(null);
      loadBatches();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const rollback = async (id) => {
    try {
      const r = await api.post("/admin/cad/rollback", { batch_id: id });
      toast.success(`Rolled back: ${r.data.restored} restored, ${r.data.deleted} deleted`);
      loadBatches();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="csv-import">
      <section className="bg-white border border-neutral-200 p-5 flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" onClick={template} data-testid="csv-template"><Download size={14} className="mr-1" /> Download template</Button>
        <Button variant="outline" size="sm" onClick={exportAll} data-testid="csv-export"><Download size={14} className="mr-1" /> Export catalogue CSV</Button>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".csv" onChange={(e) => uploadPreview(e.target.files?.[0])} className="text-xs" data-testid="csv-upload-input" />
        </div>
        <p className="text-[11px] text-neutral-500 w-full">Corrected extractor exports re-enter here — both paths converge on this one import route. Re-uploading updates in place (matched on design_number), never duplicates.</p>
      </section>

      {preview && (
        <section className="bg-white border border-neutral-200 p-5 space-y-3" data-testid="csv-preview">
          <div className="flex items-center gap-3">
            <h2 className="font-heading font-semibold">Preview — {preview.accepted} accepted, {preview.rejected} rejected</h2>
            <span className="text-xs text-neutral-500">{preview.creates} creates · {preview.updates} updates</span>
            <Button size="sm" className="ml-auto bg-stone-900 text-white" disabled={!preview.accepted || busy} onClick={commit} data-testid="csv-commit">Commit {preview.accepted} rows</Button>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-[10px] font-mono uppercase text-neutral-500 border-b">{["Row", "Design", "Title", "Action", "Errors"].map((h) => <th key={h} className="py-1.5 pr-2">{h}</th>)}</tr></thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.row} className={`border-b border-neutral-100 ${r.accepted ? "" : "bg-red-50/40"}`} data-testid={`csv-row-${r.row}`}>
                    <td className="py-1 pr-2 font-mono">{r.row}</td>
                    <td className="py-1 pr-2 font-mono">{r.design_number}</td>
                    <td className="py-1 pr-2">{r.title || r.existing_title}</td>
                    <td className="py-1 pr-2">{r.action}</td>
                    <td className="py-1 pr-2 text-red-600">{r.errors.join("; ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="bg-white border border-neutral-200 p-5" data-testid="csv-batches">
        <h2 className="font-heading font-semibold mb-3">Import batches</h2>
        {batches.map((b) => (
          <div key={b.id || b._id} className="flex items-center gap-3 border-b border-neutral-100 py-2 text-xs" data-testid={`csv-batch-${b.id || b._id}`}>
            <span className="font-mono">{b.filename}</span>
            <span className="text-neutral-500">{b.status}{b.status === "running" ? ` ${b.done}/${b.total}` : ""}</span>
            {b.status === "done" && <Button variant="ghost" size="sm" onClick={() => rollback(b.id || b._id)} data-testid={`csv-rollback-${b.id || b._id}`}><RefreshCw size={12} className="mr-1" /> Rollback</Button>}
          </div>
        ))}
        {!batches.length && <p className="text-sm text-neutral-400">No CSV imports yet.</p>}
      </section>
    </div>
  );
}
